import { useState } from 'react';
import { Formik, Form, Field } from 'formik';
import { Alert } from 'react-bootstrap';
import Axios from 'axios';
import * as Yup from 'yup';
import ErrorMessages from '../../errorConfig';
import PmsUrlConfig from '../../urlConfig';
import { sixDigitCode } from '../common';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

// Create a separate Axios instance for verification code to bypass global interceptor
const verificationAxios = Axios.create();

// Validation schema
const VerifyCodeSchema = Yup.object().shape({
  userCode: Yup.string()
    .required(ErrorMessages.VERIFICATION_CODE_REQUIRED)
    .matches(/^[0-9]+$/, "Must be only digits")
    .min(6, "Must be exactly 6 digits")
    .max(6, "Must be exactly 6 digits"),
});

// Custom hook for verification logic
export const useVerificationCode = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [serverError, setServerError] = useState('');

  const patientUser = location.state?.email || '';
  
  const handleVerificationSubmit = async (values, { setSubmitting }) => {
    try {
      const verifyCodeUrl = PmsUrlConfig.BOS_VERIFY_CODE_URL;
      const response = await verificationAxios.post(verifyCodeUrl, {
        email_id: patientUser,
        verification_code: values.userCode,
      });

      if (response.status === 200) {
        const user = response.data;
        const jwt_token = user.jwt_token;
        
        dispatch({ type: 'setJwtToken', payload: jwt_token });
        dispatch({ type: 'clearError' });
        
        navigate(`${PmsUrlConfig.CHANGE_PASSWORD_URL}`, { 
          state: { 
            message: "User verified. Proceed to change password", 
            user: user 
          } 
        });
      } else if (response.status === 400) {
        const errMessage = response.data?.error_message || "Error !! Please try again";
        navigate(`${PmsUrlConfig.FORGOT_PASSWORD_URL}`, { 
          state: { message: errMessage } 
        });
      } else if (response.status === 401) {
        setServerError("Incorrect access code. Please check and try again");
        setSubmitting(false);
      } else if (response.status === 403) {
        navigate(`${PmsUrlConfig.FORGOT_PASSWORD_URL}`, { 
          state: { message: "Access code expired. please generate code again and try" } 
        });
      }
    } catch (error) {
      console.error('Verification error:', error);
      
      if (error.response?.status === 401) {
        setServerError("Incorrect access code. Please check and try again");
        setSubmitting(false);
      } else if (error.response?.status === 403) {
        navigate(`${PmsUrlConfig.FORGOT_PASSWORD_URL}`, { 
          state: { message: "Access code expired. please generate code again and try" } 
        });
      } else {
        const errMessage = error.response?.data?.error_message || "Error !! Please try again";
        navigate(`${PmsUrlConfig.FORGOT_PASSWORD_URL}`, { 
          state: { message: errMessage } 
        });
      }
    }
  };

  const handleCodeInput = (e, setFieldValue) => {
    const verificationCode = sixDigitCode(e.target.value);
    setFieldValue('userCode', verificationCode);
  };

  return {
    patientUser,
    serverError,
    handleVerificationSubmit,
    handleCodeInput,
  };
};

// Alert Messages Component
const AlertMessages = ({ location, serverError }) => {
  return (
    <>
      {/* Show server error first (highest priority) */}
      {serverError && (
        <Alert variant="danger" className="mb-4">
          {serverError}
        </Alert>
      )}
      
      {/* Show success message only if no server error */}
      {location.state?.message && !serverError && (
        <Alert variant="success" className="mb-4">
          {location.state.message}
        </Alert>
      )}
    </>
  );
};

// Form Field Component
const VerificationCodeField = ({ errors, touched, handleCodeInput, setFieldValue }) => {
  return (
    <div className="mb-6">
      <label 
        htmlFor="userCode" 
        className="block text-sm font-medium text-gray-700 mb-2"
      >
        Enter Code <span className="text-red-500">*</span>
      </label>
      <Field
        type="text"
        id="userCode"
        name="userCode"
        placeholder="Insert your Code"
        onChange={(e) => handleCodeInput(e, setFieldValue)}
        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[rgba(11,109,118,1)] focus:border-[rgba(11,109,118,1)] transition-colors duration-200 ${
          errors.userCode && touched.userCode 
            ? 'border-red-500 bg-red-50' 
            : 'border-gray-300'
        }`}
        maxLength={6}
      />
      {errors.userCode && touched.userCode && (
        <p className="mt-2 text-sm text-red-600">
          {errors.userCode}
        </p>
      )}
    </div>
  );
};

// Submit Button Component
const SubmitButton = ({ isSubmitting }) => {
  return (
    <button
      type="submit"
      disabled={isSubmitting}
      className="w-full h-12 bg-[rgba(11,109,118,1)] hover:bg-[rgba(11,109,118,0.9)] text-white font-medium rounded-lg shadow-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {isSubmitting ? 'Verifying...' : 'Verify Code'}
    </button>
  );
};

// Login Link Component
const LoginLink = () => {
  return (
    <div className="text-center mt-4">
      <p className="text-gray-600">
        <Link 
          to={`${PmsUrlConfig.HOME_URL}`} 
          className="font-semibold text-[rgba(11,109,118,1)] hover:text-[rgba(11,109,118,0.8)] transition-colors duration-200"
        >
          Login
        </Link>
      </p>
    </div>
  );
};

// Main Form Component
const VerificationForm = ({ errors, touched, isSubmitting, handleCodeInput, setFieldValue }) => {
  return (
    <div className="space-y-6">
      <VerificationCodeField 
        errors={errors} 
        touched={touched} 
        handleCodeInput={handleCodeInput}
        setFieldValue={setFieldValue}
      />
      <SubmitButton isSubmitting={isSubmitting} />
      <LoginLink />
    </div>
  );
};

// Main Component
export default function VerifyCodeForm() {
  const {
    serverError,
    handleVerificationSubmit,
    handleCodeInput,
  } = useVerificationCode();

  const location = useLocation();

  return (
    <div className="w-full max-w-md mx-auto">
      <Formik
        initialValues={{ userCode: '' }}
        validationSchema={VerifyCodeSchema}
        onSubmit={handleVerificationSubmit}
      >
        {({ errors, touched, isSubmitting, setFieldValue }) => (
          <Form className="space-y-6" data-testid="verify-code-form">
            {/* Card Header */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200">
              <div className="border-b border-gray-200 px-6 py-4">
                <h3 className="text-xl font-semibold text-gray-900">Verify Code</h3>
              </div>

              {/* Card Body */}
              <div className="px-6 py-6">
                <AlertMessages location={location} serverError={serverError} />
                <VerificationForm 
                  errors={errors}
                  touched={touched}
                  isSubmitting={isSubmitting}
                  handleCodeInput={handleCodeInput}
                  setFieldValue={setFieldValue}
                />
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}